'use client';

import { type RefObject, useEffect, useState } from 'react';

/** Marks an element the floating daily card must never cover (the event page's order form). */
const DAILY_CARD_AVOID_ATTR = 'data-daily-card-avoid';

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

function overlaps(a: Box, b: Box): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

interface Inset {
  right: number;
  bottom: number;
}

/**
 * Whether a box pinned to the viewport's bottom-right corner, at `inset`, would
 * cover a marked element. Measured from the box's resting place, not its live
 * rect, so sliding the box away does not flip the answer back.
 */
export function useCoversMarked(ref: RefObject<HTMLElement | null>, inset: Inset, key: string) {
  const [covers, setCovers] = useState(false);
  const { right, bottom } = inset;

  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` re-measures on a new page or once the box appears.
  useEffect(() => {
    const check = () => {
      const box = ref.current;
      if (!box) return;
      const resting: Box = {
        right: window.innerWidth - right,
        bottom: window.innerHeight - bottom,
        left: window.innerWidth - right - box.offsetWidth,
        top: window.innerHeight - bottom - box.offsetHeight,
      };
      const marked = document.querySelectorAll<HTMLElement>(`[${DAILY_CARD_AVOID_ATTR}]`);
      setCovers(
        Array.from(marked).some((el) => {
          const rect = el.getBoundingClientRect();
          return rect.width > 0 && overlaps(rect, resting);
        }),
      );
    };
    check();
    // The form grows with its content and the page with its data; scrolling
    // moves a form that is not sticky yet.
    const observer = new ResizeObserver(check);
    observer.observe(document.body);
    window.addEventListener('resize', check);
    window.addEventListener('scroll', check, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', check);
      window.removeEventListener('scroll', check);
    };
  }, [ref, right, bottom, key]);

  return covers;
}
